import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '../lib/safeToast';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getApiErrorMessage } from '../lib/utils';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${base}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(getApiErrorMessage(data.detail, 'Something went wrong'));
        return;
      }
      setSent(true);
      if (data.reset_token) {
        const url = `${window.location.origin}/reset-password?token=${encodeURIComponent(data.reset_token)}`;
        setResetLink(url);
        toast.success('Demo: Use the link below or check your email.');
      } else {
        toast.success('If that email is registered, you will receive a reset link.');
      }
    } catch (err) {
      toast.error('Request failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo.png" alt="PayZen" className="w-12 h-12 rounded-xl object-contain" />
            <div>
              <h1 className="font-bold text-xl text-[#1C472B]">PayZen</h1>
              <p className="text-xs text-slate-500">Merchant Hub</p>
            </div>
          </div>

          {!sent ? (
            <>
              <h2 className="text-3xl font-extrabold text-[#0F172A] mb-2">Forgot password?</h2>
              <p className="text-slate-500 mb-8">
                Enter your email and we will send you a link to reset your password.
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="input-label">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="input-field !pl-12"
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 text-base rounded-full font-semibold bg-[#1C472B] hover:bg-[#143620] text-white shadow-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  disabled={loading}
                  data-testid="forgot-submit"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send reset link'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-extrabold text-[#0F172A] mb-2">Check your email</h2>
              <p className="text-slate-500 mb-6">
                If an account exists for <strong>{email}</strong>, we have sent a password reset link.
              </p>
              {resetLink && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 mb-6">
                  <p className="text-xs text-slate-500 mb-2">Demo: open this link to reset password</p>
                  <a href={resetLink} className="text-sm text-[#1C472B] font-medium break-all hover:underline">
                    {resetLink}
                  </a>
                </div>
              )}
              <Button
                variant="outline"
                className="w-full h-12 rounded-full font-semibold border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"
                onClick={() => navigate('/login')}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to sign in
              </Button>
            </>
          )}
          <p className="text-center mt-8 text-slate-500">
            <Link to="/login" className="text-[#1C472B] font-semibold hover:underline">Back to login</Link>
          </p>
        </div>
      </div>
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1666618207644-4de0226a3f85?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
      >
        <div className="h-full w-full bg-[#1C472B]/80 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-lg">
            <h2 className="text-4xl font-extrabold mb-4">Secure Banking for Modern Merchants</h2>
            <p className="text-lg text-slate-300">Manage payments, track transactions, and grow your business with PayZen.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
